import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Activity, ArrowRight, ArrowUpRight, GraduationCap, Megaphone, Pill, Rocket, Stethoscope, Video } from "lucide-react";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n/LangContext";

const STAT_VALUES = ["40+", "25+", "6", "12"];
const PRACTICE_ICONS = [GraduationCap, Rocket, Megaphone];
const PRACTICE_IMAGES = [IMAGES.studentsLab, IMAGES.founder, IMAGES.rally];
const PRACTICE_LINKS = ["/services#institutions", "/services#startups", "/campaigns"];
const PRACTICE_TESTIDS = ["practice-institutions", "practice-startups", "practice-campaigns"];
const TESTI_ICONS = [Stethoscope, GraduationCap];

const BentoCard = ({ className, children, testid }) => (
    <div
        data-testid={testid}
        className={`group relative overflow-hidden rounded-md border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(15,76,58,0.35)] ${className}`}
    >
        {children}
    </div>
);

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });
    const { t, lang } = useLang();
    const h = t.home;

    useEffect(() => {
        document.title = lang === "sw"
            ? "Hallomedics — Miundombinu ya Kidijitali kwa Mifumo ya Afya, Biashara na Kampeni"
            : "Hallomedics — Digital Infrastructure for Health Systems, Ventures & Campaigns";
    }, [lang]);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden pt-28 lg:pt-36" data-testid="hero-section">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
                        backgroundSize: "72px 72px",
                        maskImage: "radial-gradient(ellipse 90% 70% at 30% 20%, black 30%, transparent 75%)",
                        WebkitMaskImage: "radial-gradient(ellipse 90% 70% at 30% 20%, black 30%, transparent 75%)",
                        opacity: 0.5,
                    }}
                />
                <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pb-28">
                    <div className="lg:col-span-7">
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.05 }}
                            className="eyebrow flex items-center gap-2.5"
                            data-testid="hero-eyebrow"
                        >
                            <span className="inline-block h-2 w-2 rounded-full bg-teal animate-pulse-dot" />
                            {h.eyebrow}
                        </motion.p>

                        <h1 className="mt-7 font-heading text-4xl font-extrabold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-heading" key={lang}>
                            <MaskedLine delay={0.15}>{h.h1a}</MaskedLine>
                            <MaskedLine delay={0.27}>
                                <span className="text-primary dark:text-teal">{h.h1b}</span>
                            </MaskedLine>
                            <MaskedLine delay={0.39}>{h.h1c}</MaskedLine>
                        </h1>

                        <Reveal delay={0.55}>
                            <p className="mt-7 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="hero-subcopy">
                                {h.sub}
                            </p>
                        </Reveal>

                        <Reveal delay={0.68}>
                            <div className="mt-9 flex flex-wrap items-center gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="hero-cta-primary"
                                    className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                                >
                                    {h.ctaPrimary}
                                    <ArrowRight className="h-4 w-4" />
                                </Link>
                                <Link
                                    to="/portfolio"
                                    data-testid="hero-cta-secondary"
                                    className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-semibold text-foreground transition-colors duration-200 hover:border-primary hover:text-primary dark:hover:border-teal dark:hover:text-teal"
                                >
                                    {h.ctaSecondary}
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </Reveal>

                        <Reveal delay={0.8}>
                            <p className="mt-12 font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground" data-testid="hero-footnote">
                                {h.footnote}
                            </p>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            className="relative"
                            onMouseMove={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setTilt({
                                    x: ((e.clientY - r.top) / r.height - 0.5) * -5,
                                    y: ((e.clientX - r.left) / r.width - 0.5) * 5,
                                });
                            }}
                            onMouseLeave={() => setTilt({ x: 0, y: 0 })}
                            style={{
                                transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                                transition: "transform 0.35s ease-out",
                            }}
                            data-testid="hero-visual"
                        >
                            <div className="relative overflow-hidden rounded-md border border-border">
                                <motion.img
                                    src={IMAGES.surgeon}
                                    alt="Surgeon performing precision surgery in a modern operating room"
                                    className="aspect-[4/5] w-full scale-[1.12] object-cover"
                                    style={{ y: imgY }}
                                    loading="eager"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0F4C3A]/45 via-transparent to-transparent" aria-hidden="true" />
                            </div>

                            <motion.div
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
                                className="glass-panel absolute -left-4 bottom-8 rounded-md px-5 py-4 shadow-xl sm:-left-8"
                                data-testid="hero-uptime-badge"
                            >
                                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                    <span className="inline-block h-2 w-2 rounded-full bg-teal animate-pulse-dot" />
                                    {h.systemsLive}
                                </p>
                                <p className="mt-1.5 font-heading text-2xl font-bold tracking-tight">{h.uptime}</p>
                            </motion.div>

                            <div className="absolute right-4 top-4 rounded-full bg-terracotta px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white" data-testid="hero-hq-chip">
                                {h.hq}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <Marquee />

            {/* PRIMARY PRACTICE — HEALTH */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="health-section">
                <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
                    <div className="lg:col-span-8">
                        <Reveal>
                            <p className="eyebrow">{h.healthEyebrow}</p>
                            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
                                {h.healthH2}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-4">
                        <Reveal delay={0.1}>
                            <p className="text-base leading-relaxed text-muted-foreground">{h.healthCopy}</p>
                        </Reveal>
                    </div>
                </div>

                <div className="mt-14 grid gap-5 lg:grid-cols-6">
                    <Reveal className="lg:col-span-4 lg:row-span-2" delay={0}>
                        <BentoCard className="h-full min-h-[340px]" testid="bento-card-portal">
                            <img src={IMAGES.physician} alt="East African physician consulting a patient" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#091210]/85 via-[#091210]/20 to-transparent" aria-hidden="true" />
                            <div className="absolute bottom-0 p-7">
                                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-teal">{h.flagshipTag}</p>
                                <p className="mt-2 font-heading text-xl font-bold text-white sm:text-2xl">{h.flagshipTitle}</p>
                            </div>
                        </BentoCard>
                    </Reveal>

                    <Reveal className="lg:col-span-2" delay={0.08}>
                        <BentoCard className="h-full p-6" testid="bento-card-hmis">
                            <Activity className="h-6 w-6 text-terracotta" />
                            <h3 className="mt-4 font-heading text-lg font-semibold">{h.b1t}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.b1c}</p>
                        </BentoCard>
                    </Reveal>

                    <Reveal className="lg:col-span-2" delay={0.16}>
                        <BentoCard className="flex h-full flex-col justify-between p-6" testid="bento-card-stat">
                            <p className="font-heading text-4xl font-extrabold tracking-tight text-primary dark:text-teal sm:text-5xl">{h.statValue}</p>
                            <p className="mt-3 text-sm text-muted-foreground">{h.statLabel}</p>
                        </BentoCard>
                    </Reveal>

                    <Reveal className="lg:col-span-2" delay={0.1}>
                        <BentoCard className="h-full p-6" testid="bento-card-tele">
                            <Video className="h-6 w-6 text-terracotta" />
                            <h3 className="mt-4 font-heading text-lg font-semibold">{h.b2t}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.b2c}</p>
                        </BentoCard>
                    </Reveal>

                    <Reveal className="lg:col-span-2" delay={0.18}>
                        <BentoCard className="h-full min-h-[190px]" testid="bento-card-diagnostic">
                            <img src={IMAGES.diagnostic} alt="Health worker operating a digital diagnostic device" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#091210]/70 to-transparent" aria-hidden="true" />
                            <p className="absolute bottom-4 left-5 right-5 font-mono text-[11px] uppercase tracking-[0.18em] text-white">{h.imgCaption}</p>
                        </BentoCard>
                    </Reveal>

                    <Reveal className="lg:col-span-2" delay={0.26}>
                        <BentoCard className="h-full p-6" testid="bento-card-pharmacy">
                            <Pill className="h-6 w-6 text-terracotta" />
                            <h3 className="mt-4 font-heading text-lg font-semibold">{h.b3t}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{h.b3c}</p>
                        </BentoCard>
                    </Reveal>
                </div>
            </section>

            {/* SECONDARY PRACTICES */}
            <section className="border-t border-border bg-secondary/40" data-testid="secondary-practices">
                <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
                    <Reveal>
                        <p className="eyebrow">{h.secondaryEyebrow}</p>
                        <h2 className="mt-4 max-w-2xl font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
                            {h.secondaryH2}
                        </h2>
                    </Reveal>

                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {h.practices.map((s, i) => {
                            const Icon = PRACTICE_ICONS[i];
                            return (
                                <Reveal key={s.title} delay={i * 0.1}>
                                    <Link
                                        to={PRACTICE_LINKS[i]}
                                        data-testid={PRACTICE_TESTIDS[i]}
                                        className="group block h-full overflow-hidden rounded-md border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(15,76,58,0.35)]"
                                    >
                                        <div className="relative overflow-hidden">
                                            <img src={PRACTICE_IMAGES[i]} alt={s.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                                            <span className="absolute left-4 top-4 rounded-full bg-background/85 px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground backdrop-blur">
                                                {s.tag}
                                            </span>
                                        </div>
                                        <div className="p-6">
                                            <div className="flex items-center justify-between">
                                                <Icon className="h-5 w-5 text-terracotta" />
                                                <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-primary dark:group-hover:text-teal" />
                                            </div>
                                            <h3 className="mt-4 font-heading text-xl font-semibold tracking-tight">{s.title}</h3>
                                            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.copy}</p>
                                        </div>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* STATS */}
            <section className="border-y border-border" data-testid="stats-band">
                <div className="mx-auto grid max-w-7xl grid-cols-2 lg:grid-cols-4">
                    {STAT_VALUES.map((v, i) => (
                        <Reveal key={v} delay={i * 0.08} className={`px-6 py-12 sm:px-10 ${i > 0 ? "border-l border-border" : ""} ${i >= 2 ? "border-t border-border lg:border-t-0" : ""} ${i === 2 ? "border-l-0 lg:border-l" : ""}`}>
                            <p className="font-heading text-4xl font-extrabold tracking-tight text-primary dark:text-teal sm:text-5xl" data-testid={`stat-value-${i}`}>{v}</p>
                            <p className="mt-2 text-sm text-muted-foreground">{h.stats[i]}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="testimonials-section">
                <Reveal>
                    <p className="eyebrow">{h.testiEyebrow}</p>
                </Reveal>
                <div className="mt-10 grid gap-6 lg:grid-cols-2">
                    {h.testimonials.map((tm, i) => {
                        const Icon = TESTI_ICONS[i];
                        return (
                            <Reveal key={tm.name} delay={0.05 + i * 0.1}>
                                <figure className="h-full rounded-md border border-border bg-card p-8" data-testid={`testimonial-${i + 1}`}>
                                    <Icon className="h-6 w-6 text-terracotta" />
                                    <blockquote className="mt-5 font-heading text-xl font-medium leading-relaxed tracking-tight sm:text-2xl">
                                        {tm.quote}
                                    </blockquote>
                                    <figcaption className="mt-6 text-sm text-muted-foreground">
                                        <span className="font-semibold text-foreground">{tm.name}</span> — {tm.role}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32" data-testid="cta-section">
                <Reveal>
                    <div className="relative overflow-hidden rounded-lg bg-primary px-7 py-16 sm:px-12 lg:px-16 lg:py-20">
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0 opacity-[0.12]"
                            style={{
                                backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
                                backgroundSize: "26px 26px",
                            }}
                        />
                        <div className="relative max-w-2xl">
                            <p className="font-mono text-xs uppercase tracking-[0.22em] text-teal">{h.ctaTag}</p>
                            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-primary-foreground sm:text-4xl lg:text-5xl">
                                {h.ctaH2}
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-primary-foreground/75">{h.ctaCopy}</p>
                            <Link
                                to="/contact"
                                data-testid="cta-contact-button"
                                className="mt-8 inline-flex items-center gap-2 rounded-full bg-background px-7 py-3.5 text-sm font-semibold text-foreground transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                            >
                                {h.ctaButton}
                                <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Home;
